/**
 * Frota · Manutenção · Gestão — o painel gerencial.
 *
 * Duas perguntas: **como está a frota agora** (parados, Diário do dia,
 * preventiva vencida, semáforo por placa) e **como a manutenção andou no
 * período** (O.S. abertas × concluídas, tempo de reparo, custo, o que mais
 * reprova). O período vem do filtro global; o "agora" não depende dele.
 *
 * Gráficos: abertas × concluídas e custo são DOIS gráficos (grandezas diferentes
 * não dividem eixo). Cores validadas pelo validador do skill de dataviz nos modos
 * claro e escuro: azul #3B82F6 (abertas), verde #2E9040 (concluídas), dourado
 * #c17d09 (custo).
 */
import { useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { LoadMoreBar, useProgressiveList } from "../components/LoadMoreRow";
import { useQuery } from "@tanstack/react-query";
import { Bar, BarChart, CartesianGrid, Legend, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";
import type { MaintenanceDashboardDto, MaintenancePlateHealthDto } from "@bi/types";
import { api } from "../lib/api";
import { fmtBRL, fmtNumber } from "../lib/format";
import { useFilters, periodToDates } from "../lib/filters";
import { PageHeader } from "../components/PageHeader";
import { KPIStatCard } from "../components/KPIStatCard";
import { ChartCard } from "../components/ChartCard";
import { PLAN_STATUS } from "../features/maintenance/preventive-ui";
import { GroupSelect } from "../features/maintenance/GroupSelect";

const COR_ABERTAS = "#3B82F6";
const COR_CONCLUIDAS = "#2E9040";
const COR_CUSTO = "#c17d09";

const SAUDE: Record<MaintenancePlateHealthDto, { label: string; dot: string; badge: string }> = {
  critico: { label: "Crítico", dot: "bg-danger-600", badge: "badge-red" },
  atencao: { label: "Atenção", dot: "bg-warning-500", badge: "badge-amber" },
  ok: { label: "OK", dot: "bg-tm-500", badge: "badge-green" },
};

/** 30 h → "30 h"; 60 h → "2,5 dias". */
function duracao(h: number | null): string {
  if (h == null) return "—";
  if (h < 48) return `${fmtNumber(Math.round(h))} h`;
  return `${(h / 24).toLocaleString("pt-BR", { maximumFractionDigits: 1 })} dias`;
}

const dia = (iso: string | null) => (iso ? new Date(iso).toLocaleDateString("pt-BR", { timeZone: "America/Sao_Paulo" }) : "—");
const semana = (iso: string) => new Date(`${iso}T12:00:00Z`).toLocaleDateString("pt-BR", { day: "2-digit", month: "2-digit" });

export function MaintenanceDashboardPage() {
  const { filters } = useFilters();
  const { from, to } = periodToDates(filters.period, filters.customFrom, filters.customTo);
  const [params, setParams] = useSearchParams();
  const group = params.get("group") ?? "";

  const { data: d, isLoading } = useQuery({
    queryKey: ["maintenance", "dashboard", from, to, group],
    queryFn: () => api.get<MaintenanceDashboardDto>(`/maintenance/dashboard?from=${encodeURIComponent(from)}&to=${encodeURIComponent(to)}${group ? `&group=${group}` : ""}`),
    staleTime: 60_000,
  });

  const w = d?.workOrders;
  // O quadro começa no que pede ação: 188 placas numa lista só ninguém lê.
  const [cor, setCor] = useState<MaintenancePlateHealthDto | "todas">("critico");
  const quadro = useMemo(() => (d?.board ?? []).filter((b) => cor === "todas" || b.health === cor), [d, cor]);
  const pagina = useProgressiveList(quadro, 30);
  const semanas = (w?.weekly ?? []).map((p) => ({ ...p, label: semana(p.week) }));

  return (
    <div className="space-y-6">
      <PageHeader
        title="Gestão da Manutenção"
        subtitle="Frota própria · estado de agora e desempenho no período do filtro"
        actions={
          <div className="flex gap-2">
            <GroupSelect value={group} onChange={(g) => { const p = new URLSearchParams(params); if (g) p.set("group", g); else p.delete("group"); setParams(p, { replace: true }); }} />
            <Link className="btn-secondary" to="/frota/manutencao/ordens">Ordens de serviço</Link>
            <Link className="btn-secondary" to="/frota/manutencao/preventiva">Plano preventivo</Link>
          </div>
        }
      />

      {/* ── Agora ── */}
      <div className="grid grid-cols-2 lg:grid-cols-6 gap-4">
        <KPIStatCard label="Disponibilidade" memory={{ cardKey: "manutencao.disponibilidade", from, to, expected: d?.fleet.availabilityPct, params: { group } }} loading={isLoading}
          value={d?.fleet.availabilityPct != null ? `${d.fleet.availabilityPct.toLocaleString("pt-BR")}%` : "—"}
          subtitle={d ? `${d.fleet.stopped.length} parado(s) por O.S. · ${d.fleet.tractors} cavalos` : undefined}
          status={d?.fleet.availabilityPct != null && d.fleet.availabilityPct < 95 ? "warning" : "neutral"} />
        <KPIStatCard label="Checklist Diário hoje" memory={{ cardKey: "manutencao.diario-hoje", from, to, expected: d?.daily.done, params: { group } }} loading={isLoading}
          value={d ? `${d.daily.done} de ${d.daily.expected}` : "—"}
          subtitle={d?.daily.last7Pct != null ? `${d.daily.last7Pct.toLocaleString("pt-BR")}% nos últimos 7 dias` : undefined}
          status={d && d.daily.expected && d.daily.done < d.daily.expected ? "warning" : "neutral"} />
        <KPIStatCard label="O.S. ativas" memory={{ cardKey: "manutencao.os-ativas", expected: w?.active, params: { group } }} loading={isLoading}
          value={w ? fmtNumber(w.active) : "—"}
          subtitle={w ? `${w.overdue} atrasada(s)` : undefined}
          status={w?.overdue ? "critical" : "neutral"} />
        <KPIStatCard label="Críticas sem O.S." memory={{ cardKey: "manutencao.criticas-sem-os", expected: d?.quality.criticalWithoutWorkOrder, params: { group } }} loading={isLoading}
          value={d ? fmtNumber(d.quality.criticalWithoutWorkOrder) : "—"}
          subtitle="ocorrência grave aguardando decisão"
          status={d?.quality.criticalWithoutWorkOrder ? "critical" : "neutral"} />
        <KPIStatCard label="Preventivas vencidas" memory={{ cardKey: "manutencao.preventivas-vencidas", expected: d?.preventive.counts.vencido, params: { group } }} loading={isLoading}
          value={d ? fmtNumber(d.preventive.counts.vencido) : "—"}
          subtitle={d ? `${d.preventive.platesOverdue} placa(s) · ${fmtNumber(d.preventive.counts.sem_base)} sem base` : undefined}
          status={d?.preventive.counts.vencido ? "critical" : "neutral"} />
        <KPIStatCard label="Semáforo da frota" memory={{ cardKey: "manutencao.semaforo", from, to, expected: d?.fleet.health.critico, params: { group } }} loading={isLoading}
          value={d ? `${d.fleet.health.critico} · ${d.fleet.health.atencao} · ${d.fleet.health.ok}` : "—"}
          subtitle="crítico · atenção · ok" status="neutral" />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <div className="card">
          <h3 className="px-5 pt-4 pb-2 font-medium text-gray-900">Parados por O.S.</h3>
          {!d?.fleet.stopped.length ? (
            <p className="px-5 pb-4 text-sm text-gray-500">Nenhum veículo próprio parado por ordem de serviço.</p>
          ) : (
            <ul className="divide-y divide-gray-100">
              {d.fleet.stopped.map((s) => (
                <li key={s.plate} className="px-5 py-2.5 flex items-center justify-between text-sm">
                  <Link className="font-mono text-gray-900 hover:underline" to={`/frota/placa/${s.plate}`}>{s.plate}</Link>
                  <span className="text-gray-500">
                    há {s.days} dia(s) ·{" "}
                    <Link className="text-tm-600 hover:underline" to={`/frota/manutencao/ordens/${s.workOrderId}`}>O.S. #{s.number}</Link>
                  </span>
                </li>
              ))}
            </ul>
          )}
        </div>
        <div className="card">
          <h3 className="px-5 pt-4 pb-1 font-medium text-gray-900">Cavalos disponíveis sem Diário hoje</h3>
          <p className="px-5 pb-2 text-xs text-gray-400">O SIAG não sabe se o caminhão saiu hoje — a lista é de quem está disponível e não tem Diário.</p>
          {!d?.daily.missing.length ? (
            <p className="px-5 pb-4 text-sm text-gray-500">{d ? "Todos os cavalos disponíveis fizeram o Diário." : "—"}</p>
          ) : (
            <div className="px-5 pb-4 flex flex-wrap gap-2 max-h-48 overflow-y-auto">
              {d.daily.missing.map((m) => (
                <span key={m.plate} className="text-xs rounded-md border border-surface-border px-2 py-1 text-gray-700" title="Último Diário">
                  <span className="font-mono">{m.plate}</span>
                  <span className="text-gray-400"> · {m.lastDailyAt ? dia(m.lastDailyAt) : "nunca"}</span>
                </span>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* ── Período ── */}
      <div className="pt-2">
        <h3 className="text-base font-semibold text-gray-900">Desempenho no período</h3>
        <p className="text-sm text-gray-500">O.S. do SIAG. Custo = O.S. concluídas no período, pela data de fechamento.</p>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-5 gap-4">
        <KPIStatCard label="O.S. abertas" memory={{ cardKey: "manutencao.os-abertas-periodo", from, to, expected: w?.opened, params: { group } }} value={w ? fmtNumber(w.opened) : "—"} loading={isLoading} status="neutral" />
        <KPIStatCard label="O.S. concluídas" memory={{ cardKey: "manutencao.os-concluidas-periodo", from, to, expected: w?.completed, params: { group } }} value={w ? fmtNumber(w.completed) : "—"} loading={isLoading}
          subtitle={w?.cancelled ? `${w.cancelled} cancelada(s)` : undefined} status="neutral" />
        <KPIStatCard label="Tempo até concluir" memory={{ cardKey: "manutencao.tempo-conclusao", from, to, expected: w?.avgLeadTimeHours, params: { group } }} value={duracao(w?.avgLeadTimeHours ?? null)} loading={isLoading}
          subtitle="média da abertura ao fechamento" status="neutral" />
        <KPIStatCard label="Tempo de reparo" memory={{ cardKey: "manutencao.tempo-reparo", from, to, expected: w?.avgRepairHours, params: { group } }} value={duracao(w?.avgRepairHours ?? null)} loading={isLoading}
          subtitle="média da execução ao fechamento" status="neutral" />
        <KPIStatCard label="Custo das O.S." memory={{ cardKey: "manutencao.custo-os", from, to, expected: w?.cost.total, params: { group } }} value={w ? fmtBRL(w.cost.total) : "—"} loading={isLoading}
          subtitle={w?.cost.completedWithoutCost ? `${w.cost.completedWithoutCost} concluída(s) sem custo lançado` : undefined}
          status={w?.cost.completedWithoutCost ? "warning" : "neutral"} />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <ChartCard title="O.S. por semana" subtitle="abertas × concluídas" loading={isLoading} isEmpty={!semanas.some((s) => s.opened || s.completed)}
          emptyMessage="Nenhuma O.S. aberta ou concluída no período." className="lg:col-span-2">
          <ResponsiveContainer width="100%" height={240}>
            <BarChart data={semanas} barGap={2} margin={{ top: 8, right: 8, left: -12, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" vertical={false} />
              <XAxis dataKey="label" tick={{ fontSize: 11, fill: "#6b7280" }} axisLine={false} tickLine={false} />
              <YAxis allowDecimals={false} tick={{ fontSize: 11, fill: "#6b7280" }} axisLine={false} tickLine={false} />
              <Tooltip cursor={{ fill: "rgba(0,0,0,0.04)" }} labelFormatter={(l) => `Semana de ${l}`} />
              {/* Texto da legenda em tinta de texto; a cor da série fica no marcador. */}
              <Legend iconType="circle" iconSize={8} wrapperStyle={{ fontSize: 12 }}
                formatter={(v: string) => <span style={{ color: "#374151" }}>{v}</span>} />
              <Bar dataKey="opened" name="Abertas" fill={COR_ABERTAS} radius={[4, 4, 0, 0]} maxBarSize={28} />
              <Bar dataKey="completed" name="Concluídas" fill={COR_CONCLUIDAS} radius={[4, 4, 0, 0]} maxBarSize={28} />
            </BarChart>
          </ResponsiveContainer>
        </ChartCard>

        <div className="card p-5">
          <h3 className="font-medium text-gray-900 mb-3">Composição do custo</h3>
          {w ? (
            <dl className="space-y-3 text-sm">
              <Divisao a="Peças" va={w.cost.parts} b="Serviços" vb={w.cost.services} extra={w.cost.unitemized ? `${fmtBRL(w.cost.unitemized)} sem itens (informado à mão)` : undefined} />
              <Divisao a="Oficina própria" va={w.cost.internal} b="Terceiros" vb={w.cost.external} />
              <Divisao a="Preventiva" va={w.cost.preventive} b="Corretiva" vb={w.cost.corrective} />
            </dl>
          ) : <p className="text-sm text-gray-500">—</p>}
          <p className="text-xs text-gray-400 mt-3">O custo do ERP (O.S. do SinerSoft) está em Frota → Manutenção; não se soma a este.</p>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <ChartCard title="Custo por semana" subtitle="O.S. concluídas" loading={isLoading} isEmpty={!semanas.some((s) => s.cost)}
          emptyMessage="Nenhuma O.S. com custo concluída no período.">
          <ResponsiveContainer width="100%" height={220}>
            <BarChart data={semanas} margin={{ top: 8, right: 8, left: 8, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" vertical={false} />
              <XAxis dataKey="label" tick={{ fontSize: 11, fill: "#6b7280" }} axisLine={false} tickLine={false} />
              <YAxis tick={{ fontSize: 11, fill: "#6b7280" }} axisLine={false} tickLine={false}
                tickFormatter={(v: number) => (v >= 1000 ? `${Math.round(v / 1000)} mil` : String(v))} />
              <Tooltip cursor={{ fill: "rgba(0,0,0,0.04)" }} labelFormatter={(l) => `Semana de ${l}`} formatter={(v: number) => [fmtBRL(v), "Custo"]} />
              <Bar dataKey="cost" name="Custo" fill={COR_CUSTO} radius={[4, 4, 0, 0]} maxBarSize={36} />
            </BarChart>
          </ResponsiveContainer>
        </ChartCard>

        <div className="card lg:col-span-2">
          <div className="px-5 pt-4 pb-2 flex items-baseline justify-between">
            <h3 className="font-medium text-gray-900">O que mais reprova nos checklists</h3>
            {d && (
              <span className="text-xs text-gray-500">
                {d.quality.opened} ocorrência(s) no período · {d.quality.byCriticality.high} alta(s)
                {d.quality.avgResolutionHours != null ? ` · resolução média ${duracao(d.quality.avgResolutionHours)}` : ""}
              </span>
            )}
          </div>
          {!d?.quality.topFailingItems.length ? (
            <p className="px-5 pb-4 text-sm text-gray-500">Nenhuma ocorrência aberta no período.</p>
          ) : (
            <table className="w-full text-sm">
              <thead className="text-xs text-gray-500 uppercase bg-surface-muted">
                <tr><th className="text-left px-5 py-2">Item</th><th className="text-right px-5 py-2">Ocorrências</th><th className="text-right px-5 py-2">Altas</th></tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {d.quality.topFailingItems.map((i) => (
                  <tr key={i.itemCode}>
                    <td className="px-5 py-2 text-gray-900">
                      {i.itemLabel}
                      {i.defects.length > 0 && (
                        <span className="block text-xs text-gray-500">{i.defects.map((d) => `${d.label} ${d.count}`).join(" · ")}</span>
                      )}
                    </td>
                    <td className="px-5 py-2 text-right">{i.count}</td>
                    <td className="px-5 py-2 text-right">{i.critical || "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>

      {/* ── Quadro por placa ── */}
      <div className="card overflow-x-auto">
        <div className="px-5 pt-4 pb-2 flex flex-wrap items-start justify-between gap-3">
          <div>
            <h3 className="font-medium text-gray-900">Quadro da frota própria</h3>
            <p className="text-xs text-gray-400">
              Crítico: parado, ocorrência crítica ou preventiva vencida. Atenção: sem Diário hoje, ocorrência aberta, O.S. ativa ou preventiva vencendo.
            </p>
          </div>
          <div className="flex gap-1" role="tablist" aria-label="Filtrar por situação">
            {(["critico", "atencao", "ok", "todas"] as const).map((k) => (
              <button
                key={k}
                role="tab"
                aria-selected={cor === k}
                onClick={() => setCor(k)}
                className={`px-3 py-1.5 rounded-lg text-sm border ${cor === k ? "bg-tm-500 text-white border-tm-500" : "bg-white text-gray-600 border-surface-border hover:bg-surface-muted"}`}
              >
                {k === "todas" ? "Todas" : SAUDE[k].label}
                {d && <span className={`ml-1.5 text-xs ${cor === k ? "text-white/80" : "text-gray-400"}`}>{k === "todas" ? d.board.length : d.fleet.health[k]}</span>}
              </button>
            ))}
          </div>
        </div>
        <table className="w-full text-sm">
          <thead className="text-xs text-gray-500 uppercase bg-surface-muted">
            <tr>
              <th className="text-left px-4 py-2">Placa</th>
              <th className="text-left px-4 py-2">Situação</th>
              <th className="text-left px-4 py-2">Diário</th>
              <th className="text-right px-4 py-2">Ocorrências</th>
              <th className="text-right px-4 py-2">O.S.</th>
              <th className="text-left px-4 py-2">Preventiva</th>
              <th className="text-left px-4 py-2">Última inspeção</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {pagina.visible.map((b) => (
              <tr key={b.plate}>
                <td className="px-4 py-2.5">
                  <Link className="font-mono text-gray-900 hover:underline" to={`/frota/placa/${b.plate}`}>{b.plate}</Link>
                  <span className="text-xs text-gray-400 ml-1">{b.isTrailer ? "carreta" : "cavalo"}</span>
                </td>
                <td className="px-4 py-2.5">
                  <span className="inline-flex items-center gap-1.5">
                    <span className={`w-2 h-2 rounded-full ${SAUDE[b.health].dot}`} aria-hidden />
                    <span className="text-gray-900">{SAUDE[b.health].label}</span>
                  </span>
                  {b.reasons.length > 0 && <p className="text-xs text-gray-500">{b.reasons.join(" · ")}</p>}
                </td>
                <td className="px-4 py-2.5 text-gray-600">
                  {b.dailyToday == null ? "—" : b.dailyToday ? "feito hoje" : `último ${b.lastDailyAt ? dia(b.lastDailyAt) : "nunca"}`}
                </td>
                <td className="px-4 py-2.5 text-right">
                  {b.openNonConformities ? (
                    <Link className="hover:underline" to={`/frota/manutencao/nao-conformidades?plate=${b.plate}`}>
                      {b.openNonConformities}{b.criticalNonConformities ? ` (${b.criticalNonConformities} alta)` : ""}
                    </Link>
                  ) : "—"}
                </td>
                <td className="px-4 py-2.5 text-right">
                  {b.activeWorkOrders ? <Link className="hover:underline" to={`/frota/manutencao/ordens?plate=${b.plate}`}>{b.activeWorkOrders}</Link> : "—"}
                </td>
                <td className="px-4 py-2.5 text-xs">
                  <Link className="hover:underline text-gray-600" to={`/frota/manutencao/preventiva?plate=${b.plate}`}>
                    {[
                      b.preventiveOverdue ? `${b.preventiveOverdue} ${PLAN_STATUS.vencido.label.toLowerCase()}` : null,
                      b.preventiveDueSoon ? `${b.preventiveDueSoon} ${PLAN_STATUS.vencendo.label.toLowerCase()}` : null,
                      b.preventiveNoBase ? `${b.preventiveNoBase} sem base` : null,
                    ].filter(Boolean).join(" · ") || "em dia"}
                  </Link>
                </td>
                <td className="px-4 py-2.5 text-gray-600">{dia(b.lastInspectionAt)}</td>
              </tr>
            ))}
          </tbody>
        </table>
        {d && quadro.length === 0 && (
          <p className="px-5 py-4 text-sm text-gray-500">
            {d.board.length === 0 ? "Nenhum veículo próprio no cadastro." : "Nenhuma placa nesta situação."}
          </p>
        )}
        <LoadMoreBar list={pagina} label="placas" />
      </div>
    </div>
  );
}

/** Uma divisão do custo em duas partes, com barra de proporção e os dois valores escritos. */
function Divisao({ a, va, b, vb, extra }: { a: string; va: number; b: string; vb: number; extra?: string }) {
  const total = va + vb;
  const pa = total ? Math.round((va / total) * 100) : 0;
  return (
    <div>
      <div className="flex justify-between text-gray-700">
        <span>{a} <strong className="text-gray-900">{fmtBRL(va)}</strong></span>
        <span>{b} <strong className="text-gray-900">{fmtBRL(vb)}</strong></span>
      </div>
      <div className="h-1.5 rounded-full bg-gray-100 overflow-hidden mt-1 flex gap-0.5">
        {total > 0 && <div className="h-full rounded-full bg-gray-700" style={{ width: `${pa}%` }} />}
        {total > 0 && <div className="h-full rounded-full bg-gray-300 flex-1" />}
      </div>
      {extra && <p className="text-xs text-gray-400 mt-1">{extra}</p>}
    </div>
  );
}
